import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import { staleGuard } from "src/utils/staleGuard";

// Correcciones de fichaje propuestas por empleados pendientes de
// aprobación del admin. Slice mínimo: lista + badge para AlertsCenter.
// El listado se sirve completo desde el backend (filtrado por restaurante
// autenticado). Para volúmenes grandes se podría paginar, pero la
// frecuencia esperada es baja (1-3 por día por restaurante típico).
const initialState = {
  isLoading: false,
  items: [],
  badgeCount: 0,
  error: null,
  autoApproveSettings: null, // { enabled, threshold, maxPerMonth }
};

const slice = createSlice({
  name: "employeeCorrections",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.error = null; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload; },
    setList(state, action) {
      state.isLoading = false;
      state.items = action.payload || [];
      state.badgeCount = state.items.length;
    },
    setBadge(state, action) {
      state.badgeCount = action.payload || 0;
    },
    removeItem(state, action) {
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.badgeCount = Math.max(0, state.badgeCount - 1);
    },
    setAutoApproveSettings(state, action) {
      state.autoApproveSettings = action.payload || null;
    },
  },
});

export default slice.reducer;

// Expuesta para hidratación desde el bootstrap del TPV.
export const { setList: setEmployeeCorrectionsList } = slice.actions;

export function fetchEmployeeCorrections() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get("/clocking-adjustment/admin/pending-approvals");
      dispatch(slice.actions.setList(res.data || []));
    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || "Error"));
    }
  };
}

// Versión silenciosa para polling (no toca isLoading ni propaga error).
// Solo actualiza el badge y, si el modal está cerrado, los items se
// quedan stale hasta que el usuario abra el panel y dispare fetch.
export function pollEmployeeCorrectionsCount({ force = false } = {}) {
  return async (dispatch) => {
    if (!force && staleGuard("employeeCorrections/pending")) return;
    try {
      const res = await axios.get("/clocking-adjustment/admin/pending-approvals");
      const items = res.data || [];
      dispatch(slice.actions.setList(items));
    } catch (_) { /* silencio */ }
  };
}

export function approveEmployeeCorrection(adjustmentId) {
  return async (dispatch) => {
    await axios.post(`/clocking-adjustment/admin/approve/${adjustmentId}`);
    dispatch(slice.actions.removeItem(adjustmentId));
  };
}

export function rejectEmployeeCorrection(adjustmentId, rejectionReason) {
  return async (dispatch) => {
    await axios.post(`/clocking-adjustment/admin/reject-proposal/${adjustmentId}`, {
      rejectionReason,
    });
    dispatch(slice.actions.removeItem(adjustmentId));
  };
}

// F-AI · Bulk approve todas las correcciones fiables (score >= umbral).
export function batchApproveReliableCorrections() {
  return async (dispatch) => {
    const res = await axios.post("/clocking-adjustment/admin/batch-approve-reliable");
    dispatch(fetchEmployeeCorrections());
    return res.data?.approved || 0;
  };
}

export function fetchAutoApproveSettings() {
  return async (dispatch) => {
    try {
      const res = await axios.get("/clocking-adjustment/admin/auto-approve-settings");
      dispatch(slice.actions.setAutoApproveSettings(res.data));
    } catch (_) { /* silencio */ }
  };
}

export function saveAutoApproveSettings(dto) {
  return async (dispatch) => {
    await axios.put("/clocking-adjustment/admin/auto-approve-settings", dto);
    dispatch(slice.actions.setAutoApproveSettings(dto));
  };
}
